import { useCallback, useEffect, useState } from "react";
import api from "@/utils/axios";
import cls from "../financePage.module.scss";

const DEFAULT_LIMIT = 50;

const LedgerEntries = () => {
  const [entries, setEntries] = useState([]);
  const [filters, setFilters] = useState({ walletId: "", entryType: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchEntries = useCallback(async (activeFilters) => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("/v1/admin/finance/ledger-entries", {
        params: {
          limit: DEFAULT_LIMIT,
          walletId: activeFilters.walletId || undefined,
          entryType: activeFilters.entryType || undefined,
        },
      });
      setEntries(res.data?.data ?? []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load ledger entries",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntries({ walletId: "", entryType: "" });
  }, [fetchEntries]);

  const handleChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    fetchEntries(filters);
  };

  const resetFilters = () => {
    setFilters({ walletId: "", entryType: "" });
  };

  return (
    <div className={cls.page}>
      <header className={cls.header}>
        <div className={cls.headerLeft}>
          <p className={cls.eyebrow}>Finance • Ledger entries</p>
          <h1 className={cls.title}>Ledger entries</h1>
          <p className={cls.subtitle}>
            Audit income, expense, and liability activity across wallets.
          </p>
        </div>
      </header>

      <section className={cls.card}>
        <h2 className={cls.sectionTitle}>Filters</h2>
        <form className={cls.filters} onSubmit={applyFilters}>
          <label className={cls.formRow}>
            <span className={cls.label}>Wallet ID</span>
            <input
              className={cls.input}
              value={filters.walletId}
              onChange={handleChange("walletId")}
              placeholder="Wallet id"
            />
          </label>
          <label className={cls.formRow}>
            <span className={cls.label}>Entry type</span>
            <select
              className={cls.select}
              value={filters.entryType}
              onChange={handleChange("entryType")}
            >
              <option value="">All types</option>
              <option value="income">Income</option>
              <option value="expense">Expense</option>
              <option value="liability">Liability</option>
            </select>
          </label>
          <div className={cls.inlineActions}>
            <button className={`${cls.btn} ${cls.btnPrimary}`} type="submit">
              Apply
            </button>
            <button
              className={`${cls.btn} ${cls.btnSecondary}`}
              type="button"
              onClick={resetFilters}
            >
              Reset
            </button>
          </div>
        </form>
      </section>

      {error && (
        <div className={`${cls.stateBox} ${cls.stateBoxError}`}>
          <strong>Something went wrong</strong>
          <span>{error}</span>
        </div>
      )}

      <section className={cls.card}>
        <h2 className={cls.sectionTitle}>Entries</h2>
        {loading ? (
          <div className={cls.stateBox}>Loading ledger entries…</div>
        ) : entries.length === 0 ? (
          <div className={cls.stateBox}>No ledger entries found.</div>
        ) : (
          <div className={cls.tableWrap}>
            <table className={cls.table}>
              <thead>
                <tr>
                  <th>Wallet</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Reason</th>
                  <th>Source</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry._id}>
                    <td className={cls.code}>{entry.walletId}</td>
                    <td>{entry.entryType}</td>
                    <td>{entry.amountNoty}</td>
                    <td>{entry.reason || "-"}</td>
                    <td>
                      <span className={cls.code}>
                        {entry.sourceType || "-"} {entry.sourceId || ""}
                      </span>
                    </td>
                    <td>
                      {entry.createdAt
                        ? new Date(entry.createdAt).toLocaleString()
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default LedgerEntries;
